import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, NavLink } from "react-router-dom";
import {
  Menu,
  X,
  TrendingUp,
  Home as HomeIcon,
  Info,
  LayoutGrid,
  Briefcase,
  Workflow,
  Tag,
  Mail,
  Phone,
  ArrowLeftRight,
} from "lucide-react";
import TopBar from "@/components/layout/TopBar";
import { useCurrency } from "@/context/CurrencyContext";

const NAV = [
  { label: "Home", to: "/", icon: HomeIcon },
  { label: "About", to: "/about", icon: Info },
  { label: "Services", to: "/services", icon: LayoutGrid },
  { label: "Work", to: "/work", icon: Briefcase },
  { label: "Process", to: "/process", icon: Workflow },
  { label: "Pricing", to: "/pricing", icon: Tag },
  { label: "Contact", to: "/contact", icon: Mail },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { currency, toggle } = useCurrency();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-apex-navy/70 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <TopBar />

      <div className="container-apex flex items-center justify-between h-[68px]">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
          <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-xl bg-apex-orange/15 border border-apex-orange/30">
            <TrendingUp className="w-4.5 h-4.5 text-apex-orange" strokeWidth={2.2} />
            <span className="absolute inset-0 rounded-xl blur-md bg-apex-orange/20 group-hover:bg-apex-orange/35 transition-colors" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading font-extrabold text-xl tracking-tight">
              APEX<span className="text-apex-orange">.</span>
            </span>
            <span className="mt-0.5 text-[9px] uppercase tracking-[0.24em] text-white/55 font-semibold">
              Innovation Solutions
            </span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-0.5" aria-label="Primary">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group flex items-center gap-1.5 px-2.5 py-2 rounded-full text-[13px] font-medium transition-all ${
                    isActive
                      ? "bg-white/[0.08] text-white"
                      : "text-apex-white/75 hover:text-white hover:bg-white/[0.04]"
                  }`
                }
              >
                <Icon className="w-3.5 h-3.5 text-apex-cyan/80 group-hover:text-apex-cyan transition-colors" strokeWidth={1.8} />
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        <Link
          to="/contact"
          data-testid="nav-cta-button"
          className="hidden md:inline-flex btn-primary text-sm py-2.5 px-5"
        >
          Start a Project
        </Link>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03]"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden container-apex pb-6"
            data-testid="mobile-menu"
          >
            <div className="glass p-4 flex flex-col gap-1">
              {NAV.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm font-medium ${
                        isActive ? "bg-white/[0.08] text-white" : "text-white/85 hover:bg-white/5"
                      }`
                    }
                    data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4 text-apex-cyan/80" strokeWidth={1.8} />
                    {n.label}
                  </NavLink>
                );
              })}

              <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                <a href="tel:+917719779448" className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/80 hover:bg-white/5" data-testid="mobile-topbar-phone">
                  <Phone className="w-3.5 h-3.5 text-apex-orange" strokeWidth={2} />
                  +91 77197 79448
                </a>
                <a href="mailto:info@apexinnovationsolutions.com" className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/80 hover:bg-white/5" data-testid="mobile-topbar-email">
                  <Mail className="w-3.5 h-3.5 text-apex-cyan" strokeWidth={2} />
                  info@apexinnovationsolutions.com
                </a>
                <button
                  type="button"
                  onClick={toggle}
                  data-testid="mobile-currency-toggle"
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-white/10 bg-white/[0.03]"
                >
                  <span className="text-xs text-white/70 flex items-center gap-1.5">
                    <ArrowLeftRight className="w-3.5 h-3.5" /> Currency
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${currency === "USD" ? "bg-apex-orange text-white" : "text-white/50"}`}>USD</span>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${currency === "INR" ? "bg-apex-cyan text-apex-navy" : "text-white/50"}`}>INR</span>
                  </span>
                </button>
              </div>

              <Link
                to="/contact"
                onClick={() => setOpen(false)}
                className="btn-primary mt-3 text-sm"
                data-testid="mobile-nav-cta"
              >
                Start a Project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
